import { Context, createContext, DependencyList, useCallback, useContext, useEffect, useRef, useState } from 'react'

/**
 * HACK: Used as default value to check that context is provided at runtime.
 */
const getContextNotProvidedError =
  // Ignoring types to detect errors at runtime
  // eslint-disable-next-line no-type-assertion/no-type-assertion
  <T>() => 'Context was not provided!' as T

export const createContextRequired =
  <T>() => createContext<T>(getContextNotProvidedError())

/**
 * Use this hook along with the {@link createContextRequired} to ensure that context is provided at runtime.
 */
export const useContextRequired =
  <T>(context: Context<T>, contextName: string): T => {
    const providedContext = useContext<T>(context)

    if (providedContext === getContextNotProvidedError<T>()) {
      throw new Error(`The ${contextName} was not provided!`)
    }

    return providedContext
  }

/**
 * NOTE: This is mostly not needed and might be harmful, see https://github.com/reactwg/react-18/discussions/82.
 * Ignore the "setState on unmounted component" warnings when making API requests.
 */
export const useUnmountSignal =
  () => {
    const [abortController] = useState(() => new AbortController())

    if (abortController.signal.aborted) {
      throw new Error('Already aborted in render')
    }

    useEffect(() =>
      () => {
        if (abortController.signal.aborted) {
          throw new Error('Already aborted in uneffect')
        }

        abortController.abort()
      }, [abortController])

    return abortController.signal
  }

/**
 * Kinda like didMount, but not really.
 * Default `deps` are empty.
 * Use carefully with deps, rules of hooks are not enforced.
 * Don't depend on `func` for loader hooks - will cause infinite loading.
 */
export const useLazyEffect =
  (func: () => unknown, deps: DependencyList = []) =>
    useEffect(() => {
        // NOTE: AbortController.signal must be inline in api calls, can't isolate it to this hook
        const destructor = func()

        if (typeof destructor === 'function') {
          return () => {
            destructor()
          }
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      deps)

/**
 * Kinda like willUnmount, but not really.
 */
export const useLazyUnEffect =
  (func: () => unknown) =>
    useEffect(() =>
        () => {
          func()
        },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [])

export const useBoolState =
  (initialValue = false):
    readonly [
      value: boolean,
      setTrue: VoidFunction,
      setFalse: VoidFunction,
      toggleValue: VoidFunction
    ] => {
    const [value, setValue] = useState(initialValue)

    const setTrue = useCallback(() => setValue(true), [])
    const setFalse = useCallback(() => setValue(false), [])
    const toggleValue = useCallback(() => setValue(prevState => !prevState), [])

    return [value, setTrue, setFalse, toggleValue] as const
  }

export const useToggleState =
  (initialValue = false):
    readonly [
      value: boolean,
      toggleValue: VoidFunction
    ] => {
    const [value, setValue] = useState(initialValue)

    const toggleValue = useCallback(() => setValue(prevState => !prevState), [])

    return [value, toggleValue] as const
  }

export const usePrevious =
  <T>(value: T) => {
    const valueRef = useRef<T>(value)

    useEffect(() => {
        valueRef.current = value
      },
      [value, valueRef])

    return valueRef.current
  }

export const useIsMounted = () => {
  // Can't use state, because it's updated after unmount
  // It also needs to be a box with the value inside
  const isMounted = useRef(true)

  useEffect(() => {
    isMounted.current = true
    return () => { isMounted.current = false}
  })

  return isMounted
}